import { useCallback, useMemo, useRef, useState } from "react";
import { ToastCtx, type ToastApi, type ToastKind } from "./toast-context";
import { Icons } from "./icons";

// Lightweight toast notifications. No third-party lib — matches the shell's hand-rolled
// component style. Messages are UI feedback only (never secrets, §20).
interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

const AUTO_DISMISS_MS = 3000;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);

  const push = useCallback((kind: ToastKind, message: string) => {
    const id = ++seq.current;
    setToasts((prev) => [...prev, { id, kind, message }]);
    setTimeout(() => setToasts((prev) => prev.filter((tt) => tt.id !== id)), AUTO_DISMISS_MS);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (m) => push("success", m),
      error: (m) => push("error", m),
      info: (m) => push("info", m),
    }),
    [push],
  );

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed bottom-3 right-3 z-[100] flex flex-col gap-2" aria-live="polite" aria-atomic="false">
        {toasts.map((tt) => (
          <div
            key={tt.id}
            onClick={() => setToasts((prev) => prev.filter((x) => x.id !== tt.id))}
            className={`pointer-events-auto flex max-w-xs cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-xs shadow-2xl ${
              tt.kind === "success"
                ? "border-ok/40 bg-panel text-ok"
                : tt.kind === "error"
                  ? "border-danger/40 bg-panel text-danger"
                  : "border-border bg-panel text-fg"
            }`}
          >
            <span className="shrink-0">
              {tt.kind === "success" ? <Icons.check className="h-4 w-4" /> : tt.kind === "error" ? <Icons.close className="h-4 w-4" /> : <Icons.info className="h-4 w-4" />}
            </span>
            <span className="break-words">{tt.message}</span>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
